import {
  Banner,
  Box,
  Button,
  FlexBox,
  FormField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useLanguageRemove } from 'hooks/useLanguageRemove';
import { FieldInputText } from 'lib-knowledge-form';
import { LanguageModel } from 'lib-knowledge-management-common/utils/language';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useForm } from 'react-hook-form';

type LanguageRemoveModalProps = {
  language: LanguageModel;
};

type LanguageRemoveModalSchema = {
  languageId: string;
};
const initialLanguageRemoveModalValues: LanguageRemoveModalSchema = {
  languageId: '',
};

export function LanguageRemoveModal({
  language,
}: LanguageRemoveModalProps): JSX.Element {
  const { hideModal } = useModal();
  const { errorNotify, successNotify } = useNotification();
  const [removeLanguage, { loading }] = useLanguageRemove();

  function onRemoveLanguage() {
    removeLanguage(language)
      .then(() => {
        successNotify({
          title: 'Success',
          text: `Disabled "${language.description}" language`,
        });
        hideModal();
      })
      .catch(() => {
        errorNotify({
          text: 'Language could not be removed, please refresh and try again',
          time: 10000,
        });
      });
  }

  const { control, watch } = useForm<LanguageRemoveModalSchema>({
    defaultValues: initialLanguageRemoveModalValues,
  });
  const confirmedLanguageId = watch('languageId');
  const disabled = loading || language.languageId !== confirmedLanguageId;

  return (
    <ModalWrapper>
      <ModalHead
        title={`Remove ${language.description} Language`}
        onClose={hideModal}
      ></ModalHead>
      <ModalBody>
        <Box color="red.800" margin="0 0 300">
          <Banner
            variant="fail"
            description={
              <>
                <Text.Paragraph color="red.800">
                  Removing this language will also:
                </Text.Paragraph>
                <ul>
                  <li>
                    <Text.Paragraph color="red.800">
                      Delete any translations of articles in this language
                    </Text.Paragraph>
                  </li>
                  <li>
                    <Text.Paragraph color="red.800">
                      Delete any articles that are only provided in this
                      language
                    </Text.Paragraph>
                  </li>
                  <li>
                    <Text.Paragraph color="red.800">
                      Delete translations for text in this language like in Help
                      Centers
                    </Text.Paragraph>
                  </li>
                </ul>
                <Text.Paragraph color="red.800">
                  Once a language deleted,{' '}
                  <strong>this can not be undone</strong>.
                </Text.Paragraph>
              </>
            }
          />
        </Box>
        <FormField>
          <FieldInputText
            control={control}
            name="languageId"
            label={`To confirm, type “${language.languageId}” into the box below`}
          />
        </FormField>
      </ModalBody>
      <ModalFoot>
        <FlexBox justifyContent="flex-end" gap="200">
          <Button variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button
            onClick={onRemoveLanguage}
            variant="danger"
            disabled={disabled}
            isLoading={loading}
          >
            Remove
          </Button>
        </FlexBox>
      </ModalFoot>
    </ModalWrapper>
  );
}
